import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ListFilter } from "lucide-react";
import "../assets/css/SupportTicket.css";
import "../assets/css/Urls.css";
import { formatTicketPid } from "../utils/formatPid";
import ProjectFilterDropdown from "../components/ProjectFilterDropdown";
import FilterDropdown from "../components/FilterDropdown";
import { listProjectsLite } from "../services/projectService";

const STATUS_FILTER_OPTIONS = [
  { value: "All Status", label: "All Status" },
  { value: "open", label: "Open" },
  { value: "processing", label: "Processing" },
  { value: "closed", label: "Closed" },
];

const STATUS_STYLE = {
  open:       { bg: "#eff6ff", color: "#2563eb" },
  processing: { bg: "#fff7ed", color: "#ea580c" },
  closed:     { bg: "#f1f5f9", color: "#64748b" },
};

const ITEMS_PER_PAGE = 10;

const SuperAdminSupportTicket = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    listProjectsLite()
      .then((res) => {
        setProjects(Array.isArray(res.data?.response) ? res.data.response : []);
        // default to "All Projects"
        setSelectedProject("all");
      })
      .catch(() => {});
  }, []);

  const fetchTickets = useCallback(() => {
    setLoading(true);

    const isAll = selectedProject === "all";
    const url = isAll
      ? `${process.env.REACT_APP_BASE_URL}/api/v1/support-tickets/list-all`
      : `${process.env.REACT_APP_BASE_URL}/api/v1/support-tickets/list`;

    const body = { page, limit: ITEMS_PER_PAGE };
    if (!isAll) body.projectPid = selectedProject;
    if (search) body.search = search;
    if (statusFilter !== "All Status") body.status = statusFilter;

    fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((data) => {
        setTickets(data?.response?.items || []);
        setPagination(data?.response?.pagination || { total: 0, totalPages: 1 });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedProject, page, search, statusFilter, token]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleSearchChange = (e) => { setSearch(e.target.value); setPage(1); };
  const handleStatusChange = (val) => { setStatusFilter(val); setPage(1); };
  const handleProjectChange = (pid) => { setSelectedProject(pid); setPage(1); };

  const totalPages = Math.max(1, pagination.totalPages || 1);

  return (
    <main className="app-wrapper st-page">
      <div className="container-fluid">

        {/* Toolbar */}
        <div className="st-toolbar flex-lg-nowrap">
          <div className="st-search-wrap">
            <i className="fa-solid fa-magnifying-glass st-search-icon" />
            <input
              type="text"
              className="form-control st-search"
              placeholder="Search by subject, ID or name..."
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          <ProjectFilterDropdown
            projects={projects}
            value={selectedProject}
            onChange={handleProjectChange}
          />
          <FilterDropdown
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={handleStatusChange}
            icon={ListFilter}
            minWidth={170}
          />
        </div>

        {/* Table */}
        <div className="st-card">
          <div className="table-responsive">
            <table className="table st-table mb-0">
              <thead>
                <tr>
                  <th>Ticket ID</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Created By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-4 text-muted">Loading...</td></tr>
                ) : tickets.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-4 text-muted">No tickets found.</td></tr>
                ) : tickets.map((t) => (
                  <tr key={t.ticketPid}>
                    <td><span className="st-ticket-id">{formatTicketPid(t.ticketPid)}</span></td>
                    <td className="st-subject">{t.subject}</td>
                    <td>
                      <span
                        className="st-badge"
                        style={{
                          background: STATUS_STYLE[t.status]?.bg,
                          color: STATUS_STYLE[t.status]?.color,
                        }}
                      >
                        {t.status}
                      </span>
                    </td>
                    <td>{t.createdAt ? new Date(t.createdAt).toLocaleDateString() : ""}</td>
                    <td>
                      {t.createdBy
                        ? `${t.createdBy.firstName || ""} ${t.createdBy.lastName || ""}`.trim()
                        : ""}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button
                        className="ph-view-btn"
                        onClick={() =>
                          navigate(`/admin-support-ticket-chat/${t.ticketPid}`, { state: { ticket: t } })
                        }
                      >
                        View <ChevronRight size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="st-footer">
            <span className="st-showing">
              {pagination.total === 0
                ? "Showing 0"
                : `Showing ${(page - 1) * ITEMS_PER_PAGE + 1}–${Math.min(
                    page * ITEMS_PER_PAGE,
                    pagination.total
                  )} of ${pagination.total}`}
            </span>
            <div className="st-pagination">
              <button className="st-page-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                <i className="fa-solid fa-angle-left" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  className={`st-page-btn ${p === page ? "active" : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ))}
              <button className="st-page-btn" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                <i className="fa-solid fa-angle-right" />
              </button>
            </div>
          </div>
        </div>

      </div>
    </main>
  );
};

export default SuperAdminSupportTicket;
